import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getMintSpaceMembers, toggleModerator } from "../api/mintSpace";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import UserProfileLink from "../components/UserProfileLink";

const MintSpaceMembersPage = ({ isDark, setToast, isOwner, spaceId: propSpaceId }) => {
  const { t } = useTranslation();
  const { spaceId: paramSpaceId } = useParams();
  const spaceId = propSpaceId || paramSpaceId;
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const response = await getMintSpaceMembers(spaceId);
        if (response.success) {
          setOwner(response.data.owner);
          setMembers(response.data.members || []);
          setModerators(response.data.moderators || []);
        }
      } catch (error) {
        console.error("Error fetching members:", error);
        if (setToast) {
          setToast(t("toasts.failedLoadMembers"));
        }
      } finally {
        setLoading(false);
      }
    };

    if (spaceId) {
      fetchMembers();
    }
  }, [spaceId, setToast, t]);

  const handleToggleModerator = async (userId, isCurrentlyModerator) => {
    try {
      setUpdating({ ...updating, [userId]: true });
      const action = isCurrentlyModerator ? "remove" : "add";
      const response = await toggleModerator(spaceId, userId, action);
      
      if (response.success) {
        // Update moderators list
        if (action === "add") {
          const member = members.find(m => m._id === userId);
          if (member) {
            setModerators([...moderators, member]);
          }
        } else {
          setModerators(moderators.filter(m => m._id !== userId));
        }
        
        if (setToast) {
          setToast(action === "add" ? t("toasts.moderatorAddedSuccess") : t("toasts.moderatorRemovedSuccess"));
        }
      }
    } catch (error) {
      console.error("Error toggling moderator:", error);
      if (setToast) {
        setToast(error.response?.data?.message || t("toasts.failedModeratorUpdate"));
      }
    } finally {
      setUpdating({ ...updating, [userId]: false });
    }
  };

  const isModerator = (userId) => moderators.some((m) => m._id === userId);

  const getDisplayName = (member) => member.businessName || member.username || "User";

  /** One person: 48px avatar, name, handle and email, and whatever applies to them. */
  const Person = ({ person, children }) => (
    <div className="flex items-center gap-space-12 px-space-16 py-space-12">
      <UserProfileLink
        user={person}
        avatarSize="xl"
        avatarClassName="!size-12 !rounded-14"
        isDark={isDark}
        showAvatar
        nameClassName="sr-only"
        fallback={getDisplayName(person)}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-space-4">
        <UserProfileLink
          user={person}
          showAvatar={false}
          nameClassName="truncate text-headline text-label-primary"
          fallback={getDisplayName(person)}
        />
        <p className="truncate text-footnote text-label-secondary">
          {[person.username && `@${person.username}`, person.email].filter(Boolean).join(" · ")}
        </p>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("spaceMembers.loading")}</p>;
  }

  return (
    <div className="flex flex-col gap-space-24 p-space-8 font-ds lg:p-0">
      {owner && (
        <>
          <h2 className="text-title-3 text-label-primary">{t("spaceMembers.owner")}</h2>
          <section className="rounded-radius-lg bg-bg-raised py-space-4">
            <Person person={owner}>
              <Badge size="s" tone="success">
                {t("spaceMembers.owner")}
              </Badge>
            </Person>
          </section>
        </>
      )}

      {moderators.length > 0 && (
        <>
          <div className="flex items-center gap-space-8">
            <h2 className="text-title-3 text-label-primary">{t("spaceMembers.moderators")}</h2>
            <Badge>{moderators.length}</Badge>
          </div>
          <section className="flex flex-col divide-y divide-separator-default rounded-radius-lg bg-bg-raised">
            {moderators.map((moderator) => (
              <Person key={moderator._id} person={moderator}>
                {isOwner ? (
                  <Button
                    size="s"
                    variant="gray"
                    loading={Boolean(updating[moderator._id])}
                    onClick={() => handleToggleModerator(moderator._id, true)}
                  >
                    {t("spaceMembers.removeModerator")}
                  </Button>
                ) : (
                  <Badge size="s">{t("spaceMembers.moderator")}</Badge>
                )}
              </Person>
            ))}
          </section>
        </>
      )}

      <div className="flex items-center gap-space-8">
        <h2 className="text-title-3 text-label-primary">{t("spaceMembers.members")}</h2>
        <Badge>{members.length}</Badge>
      </div>
      {members.length === 0 ? (
        <div className="rounded-radius-lg bg-bg-raised">
          <EmptyState icon="users" title={t("spaceMembers.noMembers")} />
        </div>
      ) : (
        <section className="flex flex-col divide-y divide-separator-default rounded-radius-lg bg-bg-raised">
          {members.map((member) => (
            <Person key={member._id} person={member}>
              {user?.id === member._id && <Badge size="s">{t("spaceMembers.you")}</Badge>}
              {isOwner && !isModerator(member._id) && (
                <Button
                  size="s"
                  variant="gray"
                  loading={Boolean(updating[member._id])}
                  onClick={() => handleToggleModerator(member._id, false)}
                >
                  {t("spaceMembers.makeModerator")}
                </Button>
              )}
            </Person>
          ))}
        </section>
      )}
    </div>
  );
};

export default MintSpaceMembersPage;
